import { useCallback, useEffect, useMemo, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  AddCircle,
  CameraAlt,
  Dashboard,
  Delete,
  Edit,
  Launch,
  Refresh,
  RemoveCircleOutline,
  Save
} from "@material-ui/icons";
import { Divider, Popconfirm, Popover } from "antd";
import classnames from "classnames";
import { RootState } from "store/rootReducer";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import {
  selectCheckedGlobalTypeWells,
  selectCheckedTypeWellKeys,
  selectExpandedTypeWellKeys,
  useForecastStore
} from "stores/forecast";
import { selectTxnId, useMapStore } from "stores/map";
import styled from "styled-components/macro";
import { uid } from "utils";

import { useUser } from "hooks";
import useBetaFeatures from "hooks/useBetaFeatures";

import { DASHBOARD_TYPES, DashboardType, WorkspaceDashboard } from "models/workspace";

import { UnsavedTypeWellChangesPopconfirm } from "components/arps/UnsavedTypeWellChangesPopconfirm";
import { Tooltip } from "components/base";
import { useCloneDashboard } from "components/dashboard/hooks/useCloneDashboard";
import useDashboard from "components/dashboard/hooks/useDashboard";
import { useDeleteDashboard } from "components/dashboard/hooks/useDeleteDashboard";
import { useHideDashboard } from "components/dashboard/hooks/useHideDashboard";
import { useRenameDashboard } from "components/dashboard/hooks/useRenameDashboard";
import { useSaveDashboard } from "components/dashboard/hooks/useSaveDashboard";
import { getCreatableDashboardTypes } from "components/dashboard/utils/getCreatableDashboardTypes";
import { useScreenshotDispatch } from "components/screenshot/hooks";
import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

import NewDashboardInput from "./NewDashboardInput";
import { isDashboardScreenshottable } from "./constants/isDashboardScreenshottable";
import {
  ChartOldWidgetKey,
  MidstreamChartOldWidgetKey
} from "./constants/widgets.constants";
import { useDashboardContext, useDashboardDispatch } from "./hooks";
import { useWorkspaceDashboards } from "./hooks/useWorkspaceDashboards";
import {
  getDashboardCapabilities,
  getDashboardPermissions,
  getDashboardTypeIcon,
  getNewDashboardName
} from "./utils/";

export interface DashboardTabInputModel {
  className?: string;
  active: boolean;
  dashboard: WorkspaceDashboard;
  index: number;
  onClick: (dashboardId: string) => void;
  refreshWorkspace: () => void;
  pendingDashboardId?: string | null;
  onConfirmSwitch?: () => void;
  onCancelSwitch?: () => void;
}

export default function DashboardTab(input: DashboardTabInputModel) {
  const { hasFeature } = useBetaFeatures();
  // context
  const dashboardDispatch = useDashboardDispatch();
  const screenshotDispatch = useScreenshotDispatch();
  const {
    dashboardRefreshObserver,
    isModified,
    hasModifiedWidgets,
    dashboard: contextDashboard
  } = useDashboardContext();
  const { activeDashboardId, workspace } = useWorkspaceContext();

  const { data: dashboardData } = useDashboard(activeDashboardId);
  const currentDashboard = dashboardData?.dashboard;
  const workspaceDashboards = useWorkspaceDashboards(workspace);
  // state
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canRemove, setCanRemove] = useState(false);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [renameError, setRenameError] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmDeleteOrg, setShowConfirmDeleteOrg] = useState(false);
  const [showCopyDashboard, setShowCopyDashboard] = useState(false);
  const [showRenameDashboard, setShowRenameDashboard] = useState(false);

  // state from store
  const channel = useSelector((state: RootState) => state.channel.channel);
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const groupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const txnId = useMapStore(selectTxnId);
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const checkedTypeWellKeys = useForecastStore(selectCheckedTypeWellKeys);
  const expandedTypeWellKeys = useForecastStore(selectExpandedTypeWellKeys);

  // derived state
  const isActive = activeDashboardId === input.dashboard.dashboardId;
  const isDashboardModified = isActive && (hasModifiedWidgets || isModified);

  /**
   * To be be removed once dashboardRefreshObserver pattern is removed, we don't need it for apollo refetching anymore,
   * but we need it for now to keep context up to date for making edits (e.g editing dashboard layout)
   * */

  const refreshDashboard = useCallback(() => {
    if (dashboardRefreshObserver) {
      dashboardRefreshObserver.next(uid());
    }
  }, [dashboardRefreshObserver]);

  // mutations
  const saveDashboard = useSaveDashboard({
    onSuccess: () => {
      toast.success("Dashboard saved");
      refreshDashboard();
      // Clear unsaved-changes indicator — matches the main Save button behavior (FR-009).
      dashboardDispatch({
        payload: {
          dashboardMode: "explore",
          isModified: false,
          hasModifiedWidgets: false
        }
      });
    },
    onError: () => {
      toast.error("Failed to save dashboard");
    }
  });

  const renameDashboard = useRenameDashboard({
    onSuccess: () => {
      setShowRenameDashboard(false);
      refreshDashboard();
    },
    onError: (error) => {
      setRenameError(error.message);
    }
  });

  const cloneDashboard = useCloneDashboard({
    onSuccess: (newDashboardId) => {
      setShowCopyDashboard(false);
      refreshDashboard();
      input.onClick(newDashboardId);
    },
    onError: (error) => {
      setCopyError(error);
    }
  });

  const hideDashboard = useHideDashboard({
    onCompleted: () => {
      setContextMenuVisible(false);
    },
    onError: () => {
      toast.error("Failed to hide dashboard");
    }
  });
  const deleteDashboard = useDeleteDashboard({
    onCompleted: () => {
      setContextMenuVisible(false);
    },
    onError: () => {
      toast.error("Failed to delete dashboard");
    }
  });

  // custom hooks
  const { user, sessionId, isAtLeastPowerUser } = useUser();

  // Reset the copy error
  useEffect(() => {
    if (showCopyDashboard) setContextMenuVisible(false);
    setCopyError("");
  }, [showCopyDashboard]);

  // Reset the rename error
  useEffect(() => {
    if (showRenameDashboard) setContextMenuVisible(false);
    setRenameError("");
  }, [showRenameDashboard]);

  // Reset the component to its initial state.
  useEffect(() => {
    const dashboard = input.dashboard;

    const canEditWorkspace =
      (!!user?.id && workspace?.owner === user.id) || isAtLeastPowerUser;
    const permissions = getDashboardPermissions(
      dashboard,
      user,
      isAtLeastPowerUser,
      canEditWorkspace
    );

    const editable = permissions.canEdit;
    // Prevent removing of active dashboard.

    setCanEdit(editable);
    setCanRemove(canEditWorkspace);
    setCanDelete(permissions.canDelete);
    setCopyError("");
  }, [user, isAtLeastPowerUser, input.dashboard, input.active, workspace?.owner]);

  const handleRemoveDashboard = () => {
    if (workspace) {
      hideDashboard.mutate(input.dashboard.dashboardId, workspace);
    }
  };

  const handleDeleteDashboard = () => {
    if (workspace) {
      deleteDashboard.mutate(input.dashboard.dashboardId, workspace);
    }
  };

  // Handle the first confirmation for organization dashboards
  const handleFirstConfirmDelete = () => {
    if (input.dashboard.type === DASHBOARD_TYPES.Organization) {
      setShowConfirmDelete(false);
      setShowConfirmDeleteOrg(true);
    } else {
      handleDeleteDashboard();
    }
  };

  // Take action when the current dashboard is requested to be refreshed.
  const handleRefreshDashboard = useCallback(() => {
    setContextMenuVisible(false);
    if (!currentDashboard) return;
    // Clear the session storage cache of the chart settings.
    currentDashboard.widgets?.forEach((widget) => {
      // Keep logic for old chart widget types for backward compatibility
      if (widget.type === ChartOldWidgetKey) {
        sessionStorage.removeItem(`chart::${widget.widgetId}`);
      } else if (widget.type === MidstreamChartOldWidgetKey) {
        sessionStorage.removeItem(`midstream-chart::${widget.widgetId}`);
      } else {
        sessionStorage.removeItem(`${widget.type}::${widget.widgetId}`);
      }
    });

    // pass in empty function
    const capabilities = getDashboardCapabilities(currentDashboard, () => false);
    const hasGeoWidget = capabilities.hasXdaWidget || capabilities.hasThreeDWidget;

    if (dashboardRefreshObserver) {
      dashboardRefreshObserver.next(hasGeoWidget ? `xda-reset:${uid()}` : uid());
    }
    setContextMenuVisible(false);
  }, [currentDashboard, dashboardRefreshObserver]);

  // Take action when the current dashboard is requested to be renamed.
  const handleRenameDashboard = useCallback(
    (title: string) => {
      renameDashboard.mutate(input.dashboard.dashboardId, title);
      setContextMenuVisible(false);
    },
    [input.dashboard.dashboardId, renameDashboard]
  );

  const handleEnableDesignMode = useCallback(() => {
    dashboardDispatch({ payload: { dashboardMode: "edit" } });
    setContextMenuVisible(false);
  }, [dashboardDispatch]);

  const handlePopoutDashboard = useCallback(() => {
    if (!input.dashboard || !workspace) return;

    sessionStorage.removeItem(channel);
    const settings = {
      workspaceId: workspace?.workspaceId,
      dashboardId: input.dashboard.dashboardId
    };

    const settingsJson = JSON.stringify({
      filterId,
      groupBy,
      settings: settings,
      txnId,
      selectedWells: useMapStore.getState().selectedWells,
      typeWells: {
        checkedGlobalTypeWells,
        checkedTypeWellKeys,
        expandedTypeWellKeys
      },
      sessionId
    });
    sessionStorage.setItem(channel, settingsJson);
    window.open(`/standalone/dashboard/${channel}?channel=${channel}`);
    setContextMenuVisible(false);
  }, [
    channel,
    filterId,
    groupBy,
    txnId,
    checkedGlobalTypeWells,
    checkedTypeWellKeys,
    expandedTypeWellKeys,
    input.dashboard,
    workspace
  ]);

  // Take action when the current dashboard is saved.
  // Prefer the modified context dashboard (captures in-progress edits) over the
  // Apollo/server copy — matches the main Save button data source (FR-008).
  const handleSaveDashboard = useCallback(() => {
    const dashboardToSave = contextDashboard ?? currentDashboard;
    if (!dashboardToSave) return;

    saveDashboard.mutate(dashboardToSave);
    setContextMenuVisible(false);
  }, [contextDashboard, currentDashboard, saveDashboard]);

  // The dashboard types this user may create in the current workspace. A Personal
  // dashboard is only readable by its owner, so in an Organization workspace the copy
  // has to be an Organization one (EVA-161).
  const creatableDashboardTypes = useMemo(
    () => getCreatableDashboardTypes(workspace, isAtLeastPowerUser),
    [workspace, isAtLeastPowerUser]
  );

  const canSaveAsNewDashboard = creatableDashboardTypes.length > 0;

  // The workspace resolves after first render and can switch while Save As is already
  // open, so the dialog has to be dismissed when it stops offering a type this user can
  // create -- gating the context menu entry alone would leave an open dialog whose Save
  // does nothing. Resetting the flag rather than only hiding the dialog also keeps it
  // from reappearing unprompted if the workspace switches back.
  useEffect(() => {
    if (!canSaveAsNewDashboard) setShowCopyDashboard(false);
  }, [canSaveAsNewDashboard]);

  // Take action when the current dashboard is saved as a new dashboard. The type comes
  // from the dialog's picker so the user can see what they are creating -- in an
  // Organization workspace the copy is readable by the whole org, which must not happen
  // silently just because it is the only type left.
  const handleSaveAsNewDashboard = useCallback(
    (title: string, type?: DashboardType) => {
      const newDashboardType = creatableDashboardTypes.includes(type)
        ? type
        : creatableDashboardTypes[0];
      if (!newDashboardType) return;

      cloneDashboard.mutate(currentDashboard, title, newDashboardType, workspace);
    },
    [currentDashboard, workspace, cloneDashboard, creatableDashboardTypes]
  );

  const titleClassNames = classnames("nav", {
    "nav-active": isActive,
    plain: !isActive
  });

  const isPendingSwitch = input.pendingDashboardId === input.dashboard.dashboardId;

  return (
    <Draggable draggableId={input.dashboard.dashboardId} index={input.index}>
      {(provided) => (
        <>
          <UnsavedTypeWellChangesPopconfirm
            open={isPendingSwitch}
            placement={"bottom"}
            onConfirm={input.onConfirmSwitch}
            onCancel={input.onCancelSwitch}>
            <Popover
              overlayClassName="tab-popover"
              placement="bottom"
              content={
                <>
                  {input.active && (
                    <ContextMenuItem
                      data-testid="dashboard-tab-context-refresh"
                      onClick={() => handleRefreshDashboard()}>
                      <Refresh />
                      Refresh Settings
                    </ContextMenuItem>
                  )}

                  {input.active && canEdit && (
                    <ContextMenuItem
                      data-testid="dashboard-tab-context-save"
                      onClick={() => handleSaveDashboard()}>
                      <Save />
                      Save Settings
                    </ContextMenuItem>
                  )}

                  {input.active && canSaveAsNewDashboard && (
                    <ContextMenuItem
                      data-testid="dashboard-tab-context-save-as"
                      onClick={() => setShowCopyDashboard(true)}>
                      <AddCircle />
                      Save As New Dashboard
                    </ContextMenuItem>
                  )}

                  {hasFeature("Dashboard Screenshot") &&
                    input.active &&
                    isDashboardScreenshottable(
                      (currentDashboard?.widgets ?? [])
                        .filter(Boolean)
                        .map((w) => ({ component: w.type }))
                    ) && (
                      <ContextMenuItem
                        data-testid="dashboard-tab-context-screenshot"
                        onClick={() => {
                          const widgets = (currentDashboard?.widgets ?? []).filter(
                            Boolean
                          );
                          const widgetMetas = widgets.map((w) => ({
                            widgetId: w.widgetId,
                            title: w.title || "",
                            type: w.type
                          }));
                          useDashboardScreenshotStore.getState().activate(widgetMetas);
                          screenshotDispatch({
                            payload: {
                              widget: {
                                widgetId: activeDashboardId,
                                widgetComponentType: "dashboard"
                              }
                            }
                          });
                          setContextMenuVisible(false);
                        }}>
                        <CameraAlt />
                        Dashboard Screenshot
                      </ContextMenuItem>
                    )}

                  {input.active && <StyledDivider />}

                  {canEdit && (
                    <ContextMenuItem
                      data-testid="dashboard-tab-context-rename"
                      onClick={() => setShowRenameDashboard(true)}>
                      <Edit />
                      Rename Dashboard
                    </ContextMenuItem>
                  )}

                  {canRemove && (
                    <ContextMenuItem
                      data-testid="dashboard-tab-context-hide"
                      onClick={handleRemoveDashboard}>
                      <RemoveCircleOutline />
                      Hide Dashboard
                    </ContextMenuItem>
                  )}

                  {canDelete && (
                    <Popconfirm
                      placement="bottom"
                      className="remove-dashboard-popconfirm"
                      overlayClassName="remove-dashboard-popconfirm"
                      onConfirm={(evt) => {
                        handleFirstConfirmDelete();
                        evt.stopPropagation();
                        evt.preventDefault();
                      }}
                      onCancel={(evt) => {
                        evt.stopPropagation();
                        evt.preventDefault();
                      }}
                      open={showConfirmDelete}
                      onOpenChange={(visible) => setShowConfirmDelete(visible)}
                      okText="Delete"
                      okType="danger"
                      title={"Are you sure you want to delete this dashboard?"}>
                      <ContextMenuItem
                        data-testid="dashboard-tab-context-delete"
                        onClick={() => setShowConfirmDelete(true)}>
                        <Delete />
                        Delete Dashboard
                      </ContextMenuItem>
                    </Popconfirm>
                  )}

                  <Popconfirm
                    placement="bottom"
                    className="remove-dashboard-popconfirm"
                    overlayClassName="remove-dashboard-popconfirm"
                    onConfirm={(evt) => {
                      handleDeleteDashboard();
                      setShowConfirmDeleteOrg(false);
                      evt.stopPropagation();
                      evt.preventDefault();
                    }}
                    onCancel={(evt) => {
                      setShowConfirmDeleteOrg(false);
                      evt.stopPropagation();
                      evt.preventDefault();
                    }}
                    open={showConfirmDeleteOrg}
                    onOpenChange={(visible) => setShowConfirmDeleteOrg(visible)}
                    okText="Delete"
                    okType="danger"
                    title="This will delete the dashboard for all users in your organization. Continue?">
                    <div />
                  </Popconfirm>

                  {input.active && canEdit && (
                    <>
                      <StyledDivider />
                      <ContextMenuItem
                        data-testid="dashboard-tab-context-edit-layout"
                        onClick={() => handleEnableDesignMode()}>
                        <Dashboard />
                        Edit Dashboard Layout
                      </ContextMenuItem>
                    </>
                  )}
                  {!input.dashboard?.widgetTypes?.includes("map") && (
                    <>
                      <StyledDivider />
                      <ContextMenuItem
                        data-testid="dashboard-tab-context-new-tab"
                        onClick={() => handlePopoutDashboard()}>
                        <Launch />
                        Open in New Tab
                      </ContextMenuItem>
                    </>
                  )}
                </>
              }
              trigger="contextMenu"
              open={contextMenuVisible}
              onOpenChange={(v) => setContextMenuVisible(v)}>
              <Tooltip
                placement="bottom"
                title={
                  <>
                    {getDashboardTypeIcon(input.dashboard)}
                    {input.dashboard.title}
                  </>
                }
                mouseEnterDelay={2}>
                <DashboardTitle
                  ref={provided.innerRef}
                  className={titleClassNames}
                  isModified={isDashboardModified}
                  onClick={() => input.onClick(input.dashboard.dashboardId)}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}>
                  <span data-testid="dashboard-tab-title">{input.dashboard.title}</span>
                </DashboardTitle>
              </Tooltip>
            </Popover>
          </UnsavedTypeWellChangesPopconfirm>
          <NewDashboardInput
            setType
            typeOptions={creatableDashboardTypes}
            onAdd={(title: string, type?: DashboardType) => {
              handleSaveAsNewDashboard(title.trim(), type);
            }}
            onCancel={() => setShowCopyDashboard(false)}
            isLoading={cloneDashboard.isLoading}
            error={copyError}
            visible={showCopyDashboard && canSaveAsNewDashboard}
            title="Save As New Dashboard"
            dashboardTitle={getNewDashboardName(
              workspaceDashboards.length,
              input.dashboard.title
            )}
          />

          <NewDashboardInput
            onAdd={(title: string) => handleRenameDashboard(title.trim())}
            onCancel={() => setShowRenameDashboard(false)}
            isLoading={renameDashboard.isLoading}
            error={renameError}
            visible={showRenameDashboard}
            title="Rename Dashboard"
            dashboardTitle={`${input.dashboard?.title}`}
          />
        </>
      )}
    </Draggable>
  );
}
const DashboardTitle = styled.div`
  height: 100%;
  flex-grow: 1;
  display: inline-flex;
  align-items: center;
  color: var(--color-text-80);
  background: #fff;
  border: none;
  font-size: 1.6rem;
  font-style: ${(props) => (props.isModified ? "italic" : "normal")};
  font-weight: 400;
  padding: 0 var(--space-2);
  overflow: hidden;
  cursor: pointer;

  /* vertical divider between tabs */
  & > span::after {
    content: "";
    position: absolute;
    top: 50%;
    left: calc(-1 * var(--space-2));
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background-color: var(--color-text-10);
  }

  /* white blur on right */
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    bottom: 0;
    right: 0;
    transform: translateY(-50%);
    width: var(--space-2);
    background: linear-gradient(
      to right,
      rgba(255 255 255 / 0),
      rgba(255 255 255 / 1) 50%
    );
  }

  /* underline on active tab */
  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 3px;
    opacity: 0;
    transition: opacity 0.15s;
    background-color: var(--color-primary);
  }

  &:hover {
    color: var(--color-text);
  }
  &.nav-active {
    flex-shrink: 0;
    font-weight: 600;
    color: var(--color-text);
  }
  &.nav-active::before {
    opacity: 1;
  }

  & > span {
    white-space: nowrap;
    text-overflow: clip;
    font-style: inherit;
  }
`;

const ContextMenuItem = styled.div`
  height: 3.2rem;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-50);
  font-weight: 500;
  padding: 0 var(--space-3);
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;
